import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';

// atc-ui
import {
    Accordion,
    AccordionPanel,
    CheckboxGroup,
    Glyphicon,
    RadioGroup,
    Text,
} from 'reaxl';
import { InfoIcon } from 'reaxl-molecules';

// global ducks
import {
    kbbVRSDataDuck,
} from '@/ducks';

// bap ducks
import {
    bapActiveInteractionDuck,
    bapSelectedOptionsDuck,
} from '@/ducks/bap';

import bapTitleHelper from './utilities/bapTitleHelper';

/*
 *
 * Radio Groups and Checkbox Groups that presents a trim's options
 */
const BuildAndPriceOptions = React.memo(() => {
    const dispatch = useDispatch();
    const trimInfo = useSelector(kbbVRSDataDuck.selectors.getSelectedTrimInfo);
    const trimOptions = useSelector(kbbVRSDataDuck.selectors.getTrimOptions);
    const selectedOptions = useSelector(bapSelectedOptionsDuck.selectors.getDuckState);

    const { allOptions = [], trimId, vehicleId, vehicleIds } = trimInfo;
    const { hasMsrpOptionList = [] } = trimOptions;

    const additionalCostLabel = (<div className="text-size-200 text-subdued-lightest">Additional Cost</div>);

    // automatically select the first standard and mandatory option for each section on page load/trim change
    // if neither a standard nor mandatory option exists, make section empty
    // set active vehicle id to send to configuration service
    useEffect(() => {
        const activeOptions = {};
        if (Object.keys(trimOptions).length > 0) {
            Object.keys(bapTitleHelper).forEach((key) => {
                if (selectedOptions[key]) {
                    Object.assign(activeOptions, {
                        [key]: selectedOptions[key],
                    });
                } else if (key !== 'packageList') {
                    const standardOptions = allOptions.filter((option) => option?.categoryGroup === bapTitleHelper[key].optionsName && option.standard);

                    Object.assign(activeOptions, {
                        [key]: standardOptions[0] || null,
                    });
                } else {
                    const mandatoryOption = allOptions.filter((option) => option?.categoryGroup.includes(bapTitleHelper[key].optionsName) && option.mandatory)[0];

                    Object.assign(activeOptions, {
                        [key]: mandatoryOption ? [mandatoryOption] : null,
                    });
                }
            });
            const hasOptionsChanged = _isEqual(activeOptions, selectedOptions);

            if (!hasOptionsChanged) {
                dispatch(bapSelectedOptionsDuck.creators.setKeys(activeOptions));
                dispatch(bapActiveInteractionDuck.creators.setKeys({ vehicleId }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trimOptions, selectedOptions]);

    // store selected option with the correlating vehicle option id
    // TODO update price if driveType changes
    const handleOptionChange = (event, currentSection) => {
        event.persist();

        const availableIds = vehicleIds?.[trimId];

        // if the current radio option is a drive type, then get the options for that vehicle id drive type, otherwise
        // get the vehicle options of the currently selected drive type
        const driveTrainName = currentSection === 'availableDriveTrainList' ? event?.target?.value
            : selectedOptions?.availableDriveTrainList?.optionName;
        const newVehicleId = availableIds?.[driveTrainName];
        const newVehicleIdOptions = availableIds?.[`${driveTrainName}-options`];

        const newSelectedOptions = {};

        // Create a new object of eligible selected options that correlate to the new vehicle id.
        // Loop through all new available options - and for each option that correlates to an option section -
        // assign the current target to the new selected options object, and for all other sections that already have a
        // selection, update its option id to be valid
        if (newVehicleIdOptions) {
            newVehicleIdOptions.forEach((optionItem) => {
                for (const section in bapTitleHelper) {
                    if (optionItem?.optionName === event?.target?.value) {
                        Object.assign(newSelectedOptions, {
                            [currentSection]: optionItem,
                        });
                    } else if (optionItem?.categoryGroup === bapTitleHelper[section].optionsName
                        && !newSelectedOptions[section]
                        && optionItem?.optionName === selectedOptions[section]?.optionName) {
                        Object.assign(newSelectedOptions, {
                            [section]: optionItem,
                        });
                    }
                }
            });
        }

        dispatch(bapSelectedOptionsDuck.creators.setKeys(newSelectedOptions));
        dispatch(bapActiveInteractionDuck.creators.setKeys({
            lastTouched: currentSection,
            vehicleId: newVehicleId || vehicleId,
        }));
    };

    const handlePackagesOptionChange = (event) => {
        event.persist();
        const matchedOption = allOptions.filter((option) => option?.optionName === event?.target?.value)[0];
        const selectedPackages = selectedOptions?.packageList || [];
        const packagesArray = [...selectedPackages];

        if (selectedPackages.length !== 0 && selectedPackages.includes(matchedOption)) {
            const filteredPackages = selectedPackages.filter((pkg) => !_isEqual(pkg, matchedOption));
            dispatch(bapSelectedOptionsDuck.creators.setKeys({ packageList: filteredPackages }));
        } else {
            dispatch(bapSelectedOptionsDuck.creators.setKeys({ packageList: packagesArray.concat(matchedOption) }));
        }
        dispatch(bapActiveInteractionDuck.creators.setKeys({ lastTouched: 'packageList' }));
    };

    const setTitle = (title, list) => {
        const isColorTitle = ['exteriorColorList', 'interiorColorList'].includes(list);
        const selectedColorValue = _get(selectedOptions, list, {});

        return (
            <span
                key={`${title}-title`}
                className="margin-left-2"
                data-cmp={`${title}-title`}
            >
                {title}
                {isColorTitle && !selectedColorValue && (
                    <>
                        <Glyphicon
                            glyph="warning"
                            className="margin-horizontal-2 text-danger"
                            aria-hidden="true"
                        />
                        <Text
                            weight="normal"
                            color="subdued-lighter"
                        >
                            (0 selected)
                        </Text>
                    </>
                )}
            </span>
        );
    };

    const getOptionValues = (options) => (options || []).map((option) => ({
        label: option,
        secondaryLabel: hasMsrpOptionList.includes(option) && additionalCostLabel,
        value: option,
    }));

    const getPackageOptionsValues = (options) => (options || []).map((option) => {
        const { footer } = allOptions.find((pkg) => pkg.optionName === option);
        const renderInfoIcon = footer
            ? (
                <InfoIcon
                    infoText={footer}
                    key={`moreInfo-${option}`}
                />
            )
            : '';

        return {
            label: [option, ' ', renderInfoIcon],
            secondaryLabel: hasMsrpOptionList.includes(option) && additionalCostLabel,
            value: option,
        };
    });

    const renderPackagesGroup = (group, list) => {
        const packagesNote = (
            <Text
                className="margin-bottom-3"
                color="subdued-lighter"
                componentClass="div"
                size={300}
            >
                Note: Some packages may not be configured together for this trim.
            </Text>
        );

        // separate mandatory and regular packages
        const mandatoryOptions = [];
        const otherOptions = [];
        allOptions.forEach((option) => {
            if (option?.categoryGroup.includes('Packages') && group.includes(option.optionName)) {
                if (option.mandatory && !mandatoryOptions.includes(option.optionName)) {
                    mandatoryOptions.push(option.optionName);
                } else if (!option.mandatory && !otherOptions.includes(option.optionName)) {
                    otherOptions.push(option.optionName);
                }
            }
        });

        const renderPackageSubtitle = (title) => (
            <Text
                className="padding-top-2"
                color="subdued-lightest"
                componentClass="div"
                size={300}
                weight="bold"
            >
                {title}
            </Text>
        );

        const renderCheckboxGroup = (options) => (
            <CheckboxGroup
                hasVisual
                withCheckmark
                name={list.title}
                options={options}
                onChange={handlePackagesOptionChange}
                value={selectedOptions?.packageList?.map((pkg) => pkg.optionName) || []}
            />
        );

        return mandatoryOptions.length > 0 ? (
            <>
                {packagesNote}
                {renderPackageSubtitle('Mandatory Packages')}
                {renderCheckboxGroup(getPackageOptionsValues(mandatoryOptions))}
                {renderPackageSubtitle('Optional Packages')}
                {renderCheckboxGroup(getPackageOptionsValues(otherOptions))}
            </>
        ) : (
            <>
                {packagesNote}
                {renderCheckboxGroup(getPackageOptionsValues(group))}
            </>
        );
    };

    const renderOptionsGroup = (group, list) => {
        if (list === 'packageList') {
            return renderPackagesGroup(group, list);
        }

        return (
            <RadioGroup
                name={list.title}
                uiContext="landscape"
                containerClasses="row"
                value={selectedOptions[list]?.optionName || 'No value'}
                options={getOptionValues(group)}
                onChange={(event) => handleOptionChange(event, list)}
            />
        );
    };

    return !_isEmpty(selectedOptions) && (
        <Accordion
            bsStyle="filters"
            className="text-left"
        >
            {Object.keys(trimOptions).map((list) => {
                if (trimOptions[list].length && bapTitleHelper[list]?.title) {
                    return (
                        <AccordionPanel
                            title={setTitle(bapTitleHelper[list]?.title, list)}
                            eventKey={bapTitleHelper[list]}
                            key={`${list}-options`}
                        >
                            <span data-cmp={`cntnr-radio-${list}`}>
                                {renderOptionsGroup(trimOptions[list], list)}
                            </span>
                        </AccordionPanel>
                    );
                }
                return null;
            })}
        </Accordion>
    );
});
export default BuildAndPriceOptions;
